import { useMemo } from 'react'
import {
  CapabilityAction,
  ClubCapabilityDefinitionSchema,
  useClubCapabilitySchema,
  usePermittedCapabilities,
} from '@/services/clubCapabilities'

export interface MemberTool {
  cap: ClubCapabilityDefinitionSchema
  action: CapabilityAction
}

/**
 * The member-targeted capability actions the viewer may actually perform in a
 * club, derived from the (no-leak) capability schema gated by the per-capability
 * eligibility permission probe. The eligible members themselves are fetched lazily
 * (and paginated) by each tool, so this hook stays cheap regardless of club size.
 */
export const useClubTools = (clubId: number, enabled: boolean) => {
  const schemaQuery = useClubCapabilitySchema(clubId, enabled)

  const memberActions = useMemo(
    () =>
      (schemaQuery.data?.capabilities ?? []).flatMap((cap) =>
        cap.actions.filter((a) => a.target === 'MEMBER').map((action) => ({ cap, action }))
      ),
    [schemaQuery.data]
  )

  const capabilityKeys = useMemo(() => [...new Set(memberActions.map((t) => t.cap.key))], [memberActions])

  const { permitted, isLoading: gateLoading } = usePermittedCapabilities(
    clubId,
    capabilityKeys,
    enabled && capabilityKeys.length > 0
  )

  const tools = useMemo<MemberTool[]>(
    () => memberActions.filter((t) => permitted.has(t.cap.key)),
    [memberActions, permitted]
  )

  return { tools, isLoading: schemaQuery.isLoading || gateLoading }
}
